import type { ReactNode } from 'react';
import { formsContent } from '../../content/forms';
import { errorClasses, hintClasses, labelClasses, optionalClasses } from './fieldStyles';

/**
 * The label / hint / error frame every control on the site sits in (spec
 * section 18).
 *
 * The label is a real `<label for>` and is always visible — a placeholder
 * disappears the moment someone types, which leaves them with no way to check
 * what a field was asking for. "Optional" is stated on the optional fields
 * rather than starring the required ones, because the positive statement is
 * the one that reads correctly to a screen reader.
 *
 * The hint and the error are both wired into `aria-describedby`, so a screen
 * reader announces "ZIP code, edit text, where your horse lives, Error: enter
 * a 5 digit ZIP code" rather than leaving the message stranded next to the
 * input visually. The error message is prefixed with a word, not just coloured.
 */
export interface FieldRenderProps {
  id: string;
  /** Space-separated ids of the hint and error elements, or undefined. */
  describedBy: string | undefined;
  invalid: boolean;
}

export interface FieldProps {
  /** Must be unique in the document. Forms derive it from `useId`. */
  id: string;
  label: string;
  hint?: string;
  error?: string;
  /** Renders the visible "Optional" marker next to the label. */
  optional?: boolean;
  /**
   * Renders a `<fieldset>`/`<legend>` instead of `<label>`, for controls made
   * of several inputs (the additional-categories checkbox group).
   */
  as?: 'label' | 'group';
  children: (props: FieldRenderProps) => ReactNode;
}

export function Field({
  id,
  label,
  hint,
  error,
  optional = false,
  as = 'label',
  children,
}: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const invalid = error !== undefined;

  const labelBody = (
    <>
      {label}
      {optional ? (
        <span className={optionalClasses}>{formsContent.common.optionalLabel}</span>
      ) : null}
    </>
  );

  const description = hint ? (
    <p id={hintId} className={hintClasses}>
      {hint}
    </p>
  ) : null;

  // The "Error" prefix is for assistive technology only: it keeps the meaning
  // off colour alone without putting a punctuated label in front of every
  // message on screen (`brand-voice.md`, punctuation).
  const message = error ? (
    <p id={errorId} className={errorClasses}>
      <span className="sr-only">{formsContent.common.errorPrefix}. </span>
      {error}
    </p>
  ) : null;

  if (as === 'group') {
    // `tabIndex` so the error summary can move focus to the group as a whole —
    // there is no single control to focus, and landing on the legend is what
    // tells someone which group needs attention.
    return (
      <fieldset
        id={id}
        tabIndex={-1}
        aria-describedby={describedBy}
        className="min-w-0 border-0 p-0 focus:outline-2 focus:outline-offset-4 focus:outline-ink"
      >
        <legend className={labelClasses}>{labelBody}</legend>
        {description}
        <div className="mt-2">{children({ id, describedBy, invalid })}</div>
        {message}
      </fieldset>
    );
  }

  return (
    <div className="min-w-0">
      <label htmlFor={id} className={labelClasses}>
        {labelBody}
      </label>
      {description}
      <div className="mt-2">{children({ id, describedBy, invalid })}</div>
      {message}
    </div>
  );
}
